import React, { useCallback, useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-light-svg-icons";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Sponsor, TeamListElement, TeamListMinimum, TeamService } from "../../../../client/api";
import { callClientFunc } from "../../../../client";
import { ClavaContext } from "../../../../config/contexts";
import useBoolState from "../../../../hooks/useBoolState";
import CreateEditSponsor from "../CreateEditSponsor";
import { IDType } from "../../../../config/types";
import { errorLogging } from "../../../../config/utils";
import { translate } from "../../../../config/translator";
import ClavaImage from "../../../components/ClavaImage";
import RoundedRectangle from "../../../components/RoundedRectangle";

const SponsorManagement: React.FC<{ team: TeamListMinimum | TeamListElement }> = ({ team }) => {
  const { l } = useContext(ClavaContext);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [insert, toggleInsert, setInsert] = useBoolState(false);
  const [edit, setEdit] = useState<Sponsor>();
  const [loading, setLoading] = useState(false);

  const closeEditInsert = useCallback((newSponsor?: Sponsor | IDType) => {
    if (newSponsor) {
      if (typeof newSponsor === "number") {
        setSponsors(s => s.filter(sp => sp.id !== newSponsor));
      } else {
        setSponsors(s => {
          let found = false;
          if (s) {
            const mapped = s.map((sp) => {
              if (sp.id === newSponsor.id) {
                found = true;
                return newSponsor;
              }
              return sp;
            });
            if (!found) {
              return [newSponsor, ...mapped];
            }
            return mapped;
          }
          return [newSponsor];
        });
      }
    }
    setEdit(undefined);
    setInsert(false);
  }, [setInsert]);

  useEffect(() => {
    setLoading(true);
    callClientFunc(
      TeamService.getTeamByIdTeamTeamIdGet,
      false,
      team.id
    ).then((fullTeam) => {
      setSponsors(fullTeam.sponsors || []);
      setLoading(false);
    }, (error) => {
      errorLogging(error);
      setLoading(false);
    });
  }, [team.id]);

  return (
    <>
      {loading ? (
        <div className="text-center py-8 text-font-600 dark:text-font-600-dark">
          {translate("loading", l)}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {sponsors.map((sponsor) => (
            <div key={sponsor.id} className="w-full min-w-0 relative">
              <RoundedRectangle
                className="w-full overflow-hidden bg-bg-secondary dark:bg-bg-secondary-dark"
                hasBorder={false}
                hasTitle={false}
              >
                <div className="w-full overflow-hidden rounded-t-xl aspect-video relative bg-light-gray dark:bg-light-gray-dark flex items-center justify-center">
                  {sponsor.logo ? (
                    <ClavaImage
                      image={sponsor.logo}
                      width="100%"
                      format="full"
                      className="rounded-t-xl"
                      alt={sponsor.name}
                      objectFit="contain"
                    />
                  ) : (
                    <span className="text-font-600 dark:text-font-600-dark text-sm text-center px-2">
                      {translate("noLogo", l)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setEdit(sponsor)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEdit(sponsor);
                      }
                    }}
                    className="absolute top-2 right-2 w-9 h-9 rounded-full border border-light-gray dark:border-light-gray-dark bg-white dark:bg-bg-secondary-dark hover:bg-primary-10 flex items-center justify-center shadow-sm transition-colors"
                    aria-label={translate("modifySponsor", l)}
                  >
                    <FontAwesomeIcon icon={faPencil} className="w-4 h-4 text-font dark:text-font-dark" />
                  </button>
                </div>
              </RoundedRectangle>
            </div>
          ))}
          <div className="w-full min-w-0">
            <button
              type="button"
              onClick={toggleInsert}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleInsert();
                }
              }}
              className="w-full aspect-video rounded-xl border-2 border-primary dark:border-primary-50-dark bg-primary-50 dark:bg-primary-50-dark hover:bg-primary-10 flex flex-col items-center justify-center transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-white dark:bg-white flex items-center justify-center mb-2">
                <FontAwesomeIcon icon={faPlus} className="w-6 h-6 text-primary" />
              </div>
              <span className="text-sm text-font-600 dark:text-font-600-dark">
                {translate("addSponsor", l)}
              </span>
            </button>
          </div>
        </div>
      )}
      {insert && (
        <CreateEditSponsor close={closeEditInsert} teamId={team.id} />
      )}
      {edit && (
        <CreateEditSponsor close={closeEditInsert} sponsor={edit} teamId={team.id} />
      )}
    </>
  );
};

export default SponsorManagement;

